/**
 * Moya Fit — Dynamic PWA / iOS Home-Screen Head Metadata
 * ——————————————————————————————————————————————————————
 * Applies tenant branding to <title>, favicon, apple-touch-icon and
 * apple-mobile-web-app-title after the tenant branding payload loads.
 *
 * Notes:
 *  • Uses idempotent tag upsert — finds existing tag or creates a new one.
 *  • Safe fallbacks — if logo_url is missing, icons stay on the built-in
 *    defaults shipped in public/index.html.
 *  • Cache busting — each tag uses the tenant branding_updated_at hash so
 *    iOS picks up the new icon when the user *re-adds* to Home Screen.
 *    Already-installed Home-Screen icons do NOT update automatically on
 *    iOS — the user must remove and re-add the shortcut. (Documented in
 *    the PRD and user-facing release notes.)
 */

const DEFAULT_APP_NAME = 'Moya Fit';

function upsertLink(rel: string, href: string, attrs: Record<string, string> = {}) {
  if (!href) return;
  const selector = Object.entries(attrs).reduce<string>(
    (sel, [k, v]) => `${sel}[${k}="${v}"]`,
    `link[rel="${rel}"]`,
  );
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    document.head.appendChild(el);
  }
  if (el.getAttribute('href') !== href) el.setAttribute('href', href);
}

function upsertMeta(name: string, content: string | null | undefined) {
  if (content == null) return;
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  if (el.getAttribute('content') !== content) el.setAttribute('content', content);
}

/**
 * Resolve an absolute logo URL from the possibly-relative `logo_url` the
 * branding endpoint returns (e.g. "/api/uploads/logos/abc.jpg").
 *
 * We route logo requests through REACT_APP_BACKEND_URL when it's absolute,
 * otherwise stay on the current origin.
 */
function resolveLogoUrl(logoUrl: string | null | undefined, brandingUpdatedAt: string | null | undefined) {
  if (!logoUrl) return null;
  let url: URL;
  try {
    url = new URL(logoUrl);
  } catch {
    const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const base = backend || window.location.origin;
    url = new URL(logoUrl, base + '/');
  }
  if (brandingUpdatedAt) {
    url.searchParams.set('v', String(new Date(brandingUpdatedAt).getTime()));
  }
  return url.toString();
}

function buildInlineManifest(displayName: string, iconUrl: string | null, themeColor: string | null | undefined) {
  const manifest = {
    name: displayName,
    short_name: displayName.slice(0, 18),
    display: 'standalone',
    start_url: '/',
    scope: '/',
    background_color: '#ffffff',
    theme_color: themeColor || '#6b0f1a',
    // Android Chrome install criteria: need a 192 AND a 512 icon, at least
    // one of which is `purpose: any` OR `any maskable`. We declare the
    // tenant icon with both "any" and "maskable" purposes so it satisfies
    // both the install-prompt gate and the Android launcher's adaptive-icon
    // mask. The actual PNG the tenant uploaded may not have a 20% safe-zone
    // so Android will crop to a circle — acceptable default behaviour.
    icons: iconUrl
      ? [
          { src: iconUrl, sizes: '192x192', type: 'image/png', purpose: 'any' },
          { src: iconUrl, sizes: '512x512', type: 'image/png', purpose: 'any' },
          { src: iconUrl, sizes: '512x512', type: 'image/png', purpose: 'maskable' },
        ]
      : [],
  };
  const json = JSON.stringify(manifest);
  return 'data:application/json;charset=utf-8,' + encodeURIComponent(json);
}

interface HeadBrandingInput {
  display_name?: string | null;
  logo_url?: string | null;
  app_icon_url?: string | null;
  branding_updated_at?: string | null;
  theme_primary?: string | null;
}

export function applyTenantHeadBranding({
  display_name,
  logo_url,
  app_icon_url,
  branding_updated_at,
  theme_primary,
}: HeadBrandingInput = {}) {
  const appName = (display_name && display_name.trim()) || DEFAULT_APP_NAME;
  // Prefer dedicated square app_icon_url for PWA / favicon / iOS.
  // Fall back to logo_url so existing tenants that never uploaded a
  // separate app icon continue to work unchanged.
  const iconSource = app_icon_url || logo_url;
  const iconUrl = resolveLogoUrl(iconSource, branding_updated_at);

  // 1) document.title
  document.title = appName;

  // 2) iOS Safari Home Screen title + capability hints
  upsertMeta('apple-mobile-web-app-title', appName);
  upsertMeta('apple-mobile-web-app-capable', 'yes');
  upsertMeta('apple-mobile-web-app-status-bar-style', 'default');
  upsertMeta('mobile-web-app-capable', 'yes');
  if (theme_primary) upsertMeta('theme-color', theme_primary);

  // 3) Icons — only override when a tenant logo actually exists.
  //    Otherwise the built-in defaults in public/index.html stay in place.
  if (iconUrl) {
    // iOS touch icon (180x180 is the size Apple picks)
    upsertLink('apple-touch-icon', iconUrl, { sizes: '180x180' });
    upsertLink('apple-touch-icon', iconUrl);
    // Standard favicons
    upsertLink('icon', iconUrl);
    upsertLink('shortcut icon', iconUrl);

    // 4) PWA manifest (Android/Chrome — iOS ignores)
    const manifestHref = buildInlineManifest(appName, iconUrl, theme_primary);
    upsertLink('manifest', manifestHref);
  }
}
